import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IPlano,
  IPlanoFiltro,
  IPagerSettings,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { IPlanosResponse } from './models';

@Injectable({ providedIn: 'root' })
export class PlanosDataService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private baseUrl = `${this.environment.getApiUrl('nomoh')}/plano`;

  // Estado com Signals (PADRÃO DO PROJETO)
  private _planos = signal<IPlano[]>([]);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: '',
    ordenacao: 0,
  });
  private _filtros = signal<Omit<IPlanoFiltro, 'offSet' | 'limit'>>({
    nome: null,
    sistemaId: null,
  });

  // Signals públicos (readonly)
  $planos = this._planos.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();

  // Computed signals
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 20;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    const inicio = offSet + 1;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  // Métodos de estado
  setPlanos(response: IPlanosResponse) {
    if (response.success) {
      this._planos.set(response.data);
      this._paginacao.set(response.pagerSettings);
    } else {
      this.setErro(response.errorMessage);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<Omit<IPlanoFiltro, 'offSet' | 'limit'>>) {
    this._filtros.update((current) => ({ ...current, ...filtros }));
  }

  // Operações HTTP + Estado (API: POST /plano/findAll)
  async carregarPlanos(opcoes?: {
    offSet?: number;
    limit?: number;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const filtroDto: IPlanoFiltro & IPagerSettings = {
        ...this.$filtros(),
        offSet: opcoes?.offSet ?? pag.offSet ?? 0,
        limit: opcoes?.limit ?? pag.limit ?? 20,
        param: pag.param ?? '',
        ordenacao: pag.ordenacao ?? 0,
      };

      const response = await firstValueFrom(
        this.http.post<IPlanosResponse>(`${this.baseUrl}/findAll`, filtroDto)
      );

      this.setPlanos(response);
    } catch (error) {
      this.setErro('Erro ao carregar planos');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Criar plano (API: POST /plano/create)
  async criarPlano(plano: Omit<IPlano, 'id'>): Promise<string | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const id = await firstValueFrom(
        this.http.post<string>(`${this.baseUrl}/create`, plano)
      );

      // Recarregar lista após criar
      await this.carregarPlanos();
      return id;
    } catch (error) {
      this.setErro('Erro ao criar plano');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Buscar plano por ID (API: GET /plano/findById?id={id})
  async buscarPorId(id: string): Promise<IPlano | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const plano = await firstValueFrom(
        this.http.get<IPlano>(`${this.baseUrl}/findById?id=${id}`)
      );
      return plano;
    } catch (error) {
      this.setErro('Erro ao buscar plano');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Atualizar plano (API: PUT /plano/update)
  async atualizarPlano(plano: IPlano): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const sucesso = await firstValueFrom(
        this.http.put<boolean>(`${this.baseUrl}/update`, plano)
      );

      if (sucesso) {
        // Recarregar lista após atualizar
        await this.carregarPlanos();
      }

      return sucesso;
    } catch (error) {
      this.setErro('Erro ao atualizar plano');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // Operação: Excluir plano (API: DELETE /plano/delete/:id)
  async excluirPlano(id: string): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(this.http.delete(`${this.baseUrl}/delete/${id}`));

      // Recarregar lista após excluir
      await this.carregarPlanos();
      return true;
    } catch (error) {
      this.setErro('Erro ao excluir plano');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // Navegação de paginação
  async proximaPagina(): Promise<void> {
    if (this.$temProxPagina()) {
      const pag = this.$paginacao();
      const novoPagOffset = (pag.offSet ?? 0) + (pag.limit ?? 20);
      await this.carregarPlanos({ offSet: novoPagOffset });
    }
  }

  async paginaAnterior(): Promise<void> {
    if (this.$temPagAnterior()) {
      const pag = this.$paginacao();
      const novoPagOffset = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 20));
      await this.carregarPlanos({ offSet: novoPagOffset });
    }
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 20;
    const novoOffset = (pagina - 1) * limit;
    await this.carregarPlanos({ offSet: novoOffset });
  }

  async alterarLimit(novoLimit: number): Promise<void> {
    await this.carregarPlanos({ offSet: 0, limit: novoLimit });
  }

  async alterarOrdenacao(campo: string, direcao: 1 | -1): Promise<void> {
    this._paginacao.update((current) => ({
      ...current,
      param: campo,
      ordenacao: direcao,
    }));
    await this.carregarPlanos({ offSet: 0 });
  }

  // Busca com filtros
  async buscar(nome?: string | null, sistemaId?: string | null): Promise<void> {
    this.setFiltros({ nome, sistemaId });
    await this.carregarPlanos({ offSet: 0 }); // Voltar para primeira página
  }

  // Limpar filtros
  async limparFiltros(): Promise<void> {
    this.setFiltros({ nome: null, sistemaId: null });
    await this.carregarPlanos({ offSet: 0 });
  }

  // ========================================
  // OPERAÇÕES DE PERMISSÕES DO PLANO
  // ========================================

  /**
   * Operação: Buscar permissões de um plano
   * API: GET /plano/permissions/:id
   */
  async buscarPermissoesPlano(planoId: string): Promise<{
    planoId: string;
    permissions: Array<{
      id: string;
      key: string;
      module: string;
      action: string;
      description: string;
      availabilityId: string;
    }>;
    total: number;
  } | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const url = `${this.baseUrl}/permissions/${planoId}`;
      const result = await firstValueFrom(
        this.http.get<{
          planoId: string;
          permissions: Array<{
            id: string;
            key: string;
            module: string;
            action: string;
            description: string;
            availabilityId: string;
          }>;
          total: number;
        }>(url)
      );

      return result;
    } catch (error) {
      this.setErro('Erro ao buscar permissões do plano');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Adicionar permissões a um plano
   * API: POST /plano/permissions/add
   */
  async adicionarPermissoesPlano(
    planoId: string,
    availabilityIds: string[]
  ): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const url = `${this.baseUrl}/permissions/add`;
      const response = await firstValueFrom(
        this.http.post<{
          success: boolean;
          message: string;
          count: number;
        }>(url, { planoId, availabilityIds })
      );

      return response.success;
    } catch (error) {
      this.setErro('Erro ao adicionar permissões ao plano');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Remover permissões de um plano
   * API: DELETE /plano/permissions/remove
   */
  async removerPermissoesPlano(
    planoId: string,
    availabilityIds: string[]
  ): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const url = `${this.baseUrl}/permissions/remove`;
      const response = await firstValueFrom(
        this.http.request<{
          success: boolean;
          message: string;
          count: number;
        }>('DELETE', url, {
          body: { planoId, availabilityIds },
        })
      );

      return response.success;
    } catch (error) {
      this.setErro('Erro ao remover permissões do plano');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // OPERAÇÕES DE PLANO PADRÃO
  // ========================================

  /**
   * Operação: Definir plano como padrão do sistema
   * API: PUT /plano/setDefault/:id
   *
   * Quando um plano é marcado como padrão, ele será associado
   * automaticamente a novos usuários que se cadastrarem no sistema.
   * Apenas um plano pode ser padrão por sistema.
   */
  async setAsDefault(planoId: string): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const url = `${this.baseUrl}/setDefault/${planoId}`;
      await firstValueFrom(this.http.put<void>(url, {}));

      // Recarregar lista para atualizar o estado isDefault
      await this.carregarPlanos();
      return true;
    } catch (error) {
      this.setErro('Erro ao definir plano como padrão');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Remover plano padrão do sistema
   * API: PUT /plano/removeDefault/:id
   *
   * Remove a marcação de plano padrão. Após isso, novos usuários
   * não terão plano associado automaticamente.
   */
  async removeDefault(planoId: string): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const url = `${this.baseUrl}/removeDefault/${planoId}`;
      await firstValueFrom(this.http.put<void>(url, {}));

      // Recarregar lista para atualizar o estado isDefault
      await this.carregarPlanos();
      return true;
    } catch (error) {
      this.setErro('Erro ao remover plano padrão');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }
}
